
/**
 * The options an equipment Type picker may offer, given a unit that already
 * points at a type.
 *
 * WHY THIS IS NOT JUST THE ACTIVE LIST
 * ------------------------------------
 * `GET /equipment-types` is active-only by default, and deactivating a type
 * does NOT clear `equipment.equipment_type_id` on the units already filed under
 * it — the FK stays, and so does the derived `equipment.type` text. So a unit
 * assigned to a deactivated type opens the Edit modal with an id the option
 * list does not contain, and a `<select>` cannot display a value it has no
 * option for: it falls back to the placeholder and reads as "no type chosen"
 * for a unit that has one.
 *
 * Saving untouched is harmless — form state still holds the real id — but one
 * touch of the control reassigns the unit with nothing on screen having said it
 * already had a type. That is not cosmetic: equipment type drives the
 * inspection-type mapping (`equipmentInspectionType.ts`, API #700), so a
 * misclick changes whether the unit is flagged for inspection at all.
 *
 * The fix is the union — the live catalog plus the unit's own current type,
 * carried in as an extra option so the control can show what the row actually
 * holds. The extra option is keepable: the user may leave it alone or change it
 * deliberately, and either way the screen told the truth first.
 *
 * DEACTIVATED IS NOT RESURRECTED
 * ------------------------------
 * The extra option exists only for the id passed in as `currentTypeId`. It is
 * never offered on a new unit, never offered for any other retired type, and
 * nothing here writes to the catalog. A type the tenant withdrew stays
 * withdrawn for every unit that was not already on it.
 */
/**
 * The three fields this builder reads. Declared structurally rather than
 * importing `EquipmentType`, because the screen carries its own narrower
 * catalog shape and widening it here would mean a cast at the call site — which
 * is a lie about the data, written to satisfy the compiler.
 */
export interface EquipmentTypeLike {
  id: string;
  name: string;
  isActive: boolean;
}

export interface EquipmentTypeOption {
  id: string;
  /** What the `<option>` renders — carries the "(inactive)" suffix, if any. */
  label: string;
  /**
   * The catalog name with no suffix. This is what `equipment.type` should be
   * set to when the option is picked; writing `label` there would persist the
   * decoration into the text column.
   */
  name: string;
  /**
   * Carried only because the unit already points at it. The active catalog does
   * not offer it, and exactly one option in a list can have this set.
   */
  isOffList: boolean;
}

/** Absent, empty, or whitespace-only — a non-value in the text columns. */
function firstRealString(...candidates: Array<string | null | undefined>): string | null {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() !== '') return c.trim();
  }
  return null;
}

/**
 * Build the picker's options: the active catalog, plus the unit's current type
 * when that type is not in the catalog.
 *
 * @param activeTypes   the live catalog, already sorted — returned order is preserved
 * @param currentTypeId `equipment.equipment_type_id`, or null/'' on a unit with no type
 * @param allTypes      the catalog including deactivated rows (`?include_inactive=true`).
 *                      May be empty while that request is in flight; the fallback covers it.
 * @param fallbackName  `equipmentTypeName` or the legacy `type` text off the unit
 *                      itself, used to label the extra option when `allTypes`
 *                      cannot name it
 */
export function buildEquipmentTypeOptions(
  activeTypes: EquipmentTypeLike[],
  currentTypeId: string | null | undefined,
  allTypes: EquipmentTypeLike[] = [],
  fallbackName?: string | null,
): EquipmentTypeOption[] {
  const options: EquipmentTypeOption[] = activeTypes.map(t => ({
    id: t.id,
    label: t.name,
    name: t.name,
    isOffList: false,
  }));

  const id = typeof currentTypeId === 'string' ? currentTypeId.trim() : '';
  if (!id) return options;
  if (options.some(o => o.id === id)) return options;

  /*
   * Label it from the catalog where the catalog can name it, and say "inactive"
   * only on the evidence of an `isActive === false` row. An id we cannot
   * resolve is labelled "not in list" instead — a weaker claim, because it is
   * the only one the data supports. Claiming "inactive" for a row we never saw
   * would be a guess rendered as a fact, which is the same failure as rendering
   * a default for an absent value.
   */
  const known = allTypes.find(t => t.id === id) ?? null;
  const name = firstRealString(known?.name, fallbackName);
  const shown = name ?? 'Current type';
  const label = known
    ? (known.isActive ? shown : `${shown} (inactive)`)
    : `${shown} (not in list)`;

  options.push({
    id,
    label,
    // Never an empty string: the picker writes this into `equipment.type`, and
    // blanking that column on a unit that has a type is the bug in miniature.
    name: name ?? '',
    isOffList: true,
  });
  return options;
}
